import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { AppBaseComponent } from "@shared/components/app-component-base";
import { ApiService } from "@core/services/api.service";
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  Subject,
  Subscription,
} from "rxjs";
import { MatLegacyCheckboxChange as MatCheckboxChange } from "@angular/material/legacy-checkbox";
import { AppConstants } from "@core/constants";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { TestService } from "@core/services/test/test.service";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  tap,
} from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { FileHandle } from "@shared/directives/drag-file/drag-file.directive";
import { FilesService } from "@core/services/files/files.service";
import { OptimizationOptionDialogComponent } from "@shared/dialogs/optimization-option-dialog/optimization-option-dialog.component";
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from "@shared/dialogs/confirm-dialog/confirm-dialog.component";
import { String } from "typescript-string-operations";
import { AutocompleteTagsEnum } from "@shared/components/chips/autocomplete-tags.interface";
import { FitFileParserService } from "@shared/services/fit-file-parser.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MixpanelService } from "@core/services/mixpanel.service";
import { TestTypeStorageService } from "@shared/services/test-type-storage/test-type-storage.service";
import { ManageAthleteDetailDialogComponent } from "@shared/components/manage-athlete-detail-dialog/manage-athlete-detail-dialog.component";
import { DefaultSettingDialogComponent } from "@shared/dialogs/default-setting-dialog/default-setting-dialog.component";
import { Location } from "@angular/common";
import { AthletesService } from "@core/services/athletes-management/athletes.service";
import { TestEquipmentService } from "@shared/services/test-equipment/test-equipment.service";
import { TEST_EQUIPMENT_CONTEXT_CONSTANT } from "@shared/enums/test-equipment-context.constant";
import { TestEqupimentListSource } from "@shared/interfaces/test-equipment-list-params.interface";
import { WaffleFeatureFlagsService } from "@shared/services/feature-flags/waffle-feature-flags.service";
import { FeatureFlagEnum } from "@shared/enums/feature-flag.enum";
import { ApiSecureService } from "@core/services/api/api-secure.service";
import { TxtFileParserService } from "@shared/services/txt-file-parser.service";
import { HelperService } from "@shared/services/helper.service";
import { FeatureManagementService } from "@shared/services/feature-management/feature-management.service";
import { OnboardingService } from "@shared/services/onboarding/onboarding.service";
import { FeatureSlugEnum } from "@shared/enums/feature-slug.enum";

@Component({
  selector: "app-test-creation",
  templateUrl: "./test-creation.component.html",
  styleUrls: ["./test-creation.component.scss"],
})
export class TestCreationComponent
  extends AppBaseComponent
  implements OnDestroy
{
  private mixpanelService: MixpanelService = inject(MixpanelService);
  private testTypeStorageService: TestTypeStorageService = inject(
    TestTypeStorageService,
  );
  private athletesService: AthletesService = inject(AthletesService);
  private testEquipmentService: TestEquipmentService =
    inject(TestEquipmentService);
  private txtFileParserService: TxtFileParserService =
    inject(TxtFileParserService);
  private helperService: HelperService = inject(HelperService);
  private onboardingService: OnboardingService = inject(OnboardingService);

  public data: any;
  public listFileUpload: Array<any> = [];
  public testId: any;
  public testData: { [x: string]: any } = {};
  public fileToUpload: File | undefined;
  public isShowAdvancedSetting: boolean = false;
  public showEquipment: boolean = false;
  public isProductionEnvironment: boolean = false;
  public isShowEquipmentUsedSetting: boolean = false;
  public isShowDocumentationAndEnvironment: boolean = false;
  public isShowExpertSetting: boolean = false;
  public isShowEnergySetting: boolean = false;
  public isShowTestSetting: boolean = false;
  public isChecked: boolean = false;
  public allTags: Array<any> = [];
  public coaches: any;
  public allSports: any;
  public filteredAthletes: any;
  public athletes: any;
  public filteredTests: any;
  public testForm: UntypedFormGroup = this.fb.group({});
  public formConditions: UntypedFormGroup = {} as UntypedFormGroup;
  public egbA: any;
  public egbB: any;
  public egbD: any;
  public eqA: any;
  public eqB: any;
  public eqD: any;
  public back: any;
  public backTestDataLoaded: boolean = false;
  public initDataLoaded: boolean = false;
  private testDataLoaded$: Subject<boolean> = new Subject<boolean>();
  public testDataLoadedSubscription: Subscription = new Subscription();
  public searchAthleteCtrl: UntypedFormControl = new UntypedFormControl("");
  public autocompleteTagsEnum = AutocompleteTagsEnum;
  public newTagList: Array<string> = [];
  public selectedValueCopy: any = {};
  public currentAthlete: any;
  public testEquipmentTileList: any[] = [];
  public originalDeviceList: any[] = [];
  public testCreationType: any = TEST_EQUIPMENT_CONTEXT_CONSTANT.TEST_CREATION;
  public payload: any = {
    equipment: [],
  };
  public currentSport: any;

  // Equipment is re-loaded after an athlete+sport combination is selected
  public selectedAthlete?: number;
  public selectedSportSlug?: string;
  public equipmentLoadedParams: Record<string, any> = {
    athlete: undefined,
    sportSlug: undefined,
  };
  public isOnboardingComplete$: BehaviorSubject<boolean> =
    this.onboardingService.getOnboardingStatus();
  public onboardingProgress: BehaviorSubject<number> =
    this.onboardingService.getOnboardingPercentage();

  @ViewChild("fileUpload") fileUpload!: ElementRef;

  constructor(
    private fb: UntypedFormBuilder,
    private apiService: ApiService,
    private apiSecureService: ApiSecureService,
    private snackBar: MatSnackBar,
    private testService: TestService,
    private readonly filesService: FilesService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly fitFileParserService: FitFileParserService,
    private destroyRef: DestroyRef,
    private location: Location,
    private featureFlagService: WaffleFeatureFlagsService,
    private featureManagementService: FeatureManagementService,
  ) {
    super();
  }

  public openModalWindowCreateNewAthlete(): void {
    this.dialog
      .open(ManageAthleteDetailDialogComponent, {
        width: "800px",
        panelClass: "general-dialog",
        autoFocus: false,
        disableClose: true,
        data: { isShowDetail: true },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res): void => {
        this.mixpanelService.stop_session_recording();
        if (res) {
          const newAthleteId: number = JSON.parse(res.response).id;
          this.initOptionAndValue().subscribe({
            complete: () => {
              this.onChangeAthlete(newAthleteId);
            },
          });
          this.testForm.get("athlete")?.setValue(newAthleteId);
          if (this.data.isPPD) {
            this.getTestsForAthlete(newAthleteId);
            this.testForm.get("files")?.setValue([]);
          }

          this.snackBar.open(
            "The athlete created successfully",
            "OK",
            AppConstants.TOAST_CONFIG.SUCCESS,
          );
        }
      });
  }

  public onInitPage(): void {
    this.isProductionEnvironment = this.helperService.isProductionEnvironment(
      location,
      AppConstants.ENVIRONMENT_URL.PROD,
    );
    this.data = this.testTypeStorageService.getTestConfig();

    if (!this.data) {
      this.router.navigate(["/home/dashboard"]);
      return;
    }

    const equipmentFeatureEnabled =
      this.featureManagementService.getFeatureValue(
        FeatureSlugEnum.TESTING_EQUIPMENT,
      );
    const testTypeValid = this.data.isLactate || this.data.isVirtual;
    this.showEquipment = equipmentFeatureEnabled && testTypeValid;

    this.initPage();
  }

  public onChangeEquipment(device: any): void {
    this.payload = this.testEquipmentService.generateEquipmentPayload(
      device,
      this.payload,
      this.testData.athlete,
    );
  }

  public initPage(): void {
    this.initForm();
    this.initOptionAndValue().subscribe();
    this.testDataLoadedSubscription = this.testDataLoaded$.subscribe(
      (): void => {
        this.testForm.patchValue(this.testData, { emitEvent: false });
      },
    );

    if (this.data) {
      this.testId = this.data.testId;
      this.back = this.data.isBack;

      if ((this.data?.isLactate || this.data?.isPPD) && this.data?.isBack) {
        this.testService
          .getBackTestById(this.testId)
          .subscribe((res: any): void => {
            this.testData = res.results[0];
            this.testForm.patchValue(this.testData, { emitEvent: false });
            this.backTestDataLoaded = true;
            if (this.data?.isPPD) {
              this.getTestsForAthlete(this.testData.athlete);
              this.testForm.get("files")?.setValue([]);
            }
          });
      } else {
        this.backTestDataLoaded = true;
      }

      if (this.data.isVirtual) {
        this.testService
          .getTestById(this.testId)
          .subscribe((res: any): void => {
            this.testData = res;
            this.testData.vlamax = +res.vlamax.toFixed(2);
            this.testData.vo2max_min = +res.vo2max_min.toFixed(2);

            // Hide equipment used if this is not a virtual test for a lactate test
            this.showEquipment = res.test_type === "lactate";
          });
      }

      this.searchAthleteCtrl.valueChanges
        .pipe(debounceTime(450), distinctUntilChanged())
        .subscribe((): void => {
          this._applyFilter();
        });
    }
  }

  public ngOnDestroy(): void {
    if (this.testDataLoadedSubscription) {
      this.testDataLoadedSubscription.unsubscribe();
    }
  }

  public prepareEditConditions(conditions: any): UntypedFormArray {
    const dataCondition = JSON.parse(conditions);
    if (dataCondition && dataCondition.length) {
      dataCondition.forEach((item: any): void => {
        this.conditions.push(this.newConditions(item));
      });
    }
    return this.conditions;
  }

  private initForm(): void {
    this.formConditions = this.fb.group({
      conditions: this.fb.array([]),
    });

    this.testForm = this.fb.group({
      is_default_organization: new UntypedFormControl(false),
      is_default_athlete: new UntypedFormControl(false),
      athlete: new UntypedFormControl("", Validators.required),
      sport: new UntypedFormControl("", Validators.required),
      test_date: new UntypedFormControl("", Validators.required),
      coach: new UntypedFormControl(this.auth.id, Validators.required),
      location: new UntypedFormControl(""),
      tags: new UntypedFormControl("", this.validateTags),
      comment: new UntypedFormControl(""),

      mass: new UntypedFormControl("", [
        Validators.required,
        Validators.min(10),
        Validators.max(200),
      ]),
      height: new UntypedFormControl("", [
        Validators.required,
        Validators.max(300),
        Validators.min(100),
      ]),
      proz_body_fat_100: new UntypedFormControl("", [
        Validators.required,
        Validators.max(60),
        Validators.min(3),
      ]),

      auto_calc_body_com: new UntypedFormControl(true, []),
      proz_body_water_100_override: new UntypedFormControl("", [
        Validators.max(99),
        Validators.min(1),
      ]),
      proz_body_muscle_100_override: new UntypedFormControl("", [
        Validators.max(99),
        Validators.min(1),
      ]),
      proz_muscle_mass_used_100_override: new UntypedFormControl("", [
        Validators.max(99),
        Validators.min(1),
      ]),
      glycogen_content: new UntypedFormControl("", [
        Validators.max(40),
        Validators.min(13),
      ]),

      //MANUAL TESTS
      vo2max_min: new UntypedFormControl("", [
        Validators.max(150),
        Validators.min(1),
        Validators.pattern(AppConstants.REGEX.DECIMAL_2),
      ]),
      vlamax: new UntypedFormControl("", [
        Validators.max(3),
        Validators.min(0),
        Validators.pattern(AppConstants.REGEX.DECIMAL_2),
      ]),

      //PPD TESTS
      files: new UntypedFormControl([]),

      au_override: new UntypedFormControl("", [
        Validators.max(20),
        Validators.min(-1),
      ]),
      bu_override: new UntypedFormControl("", Validators.min(0)),
      du_override: new UntypedFormControl("", Validators.min(0)),
      o2_per_watt_eq_a: new UntypedFormControl("", Validators.min(0)),
      o2_per_watt_eq_b: new UntypedFormControl("", Validators.min(0)),
      o2_per_watt_eq_d: new UntypedFormControl("", Validators.min(0)),
      power_meter: new UntypedFormControl(""),
      lactate_analyzer: new UntypedFormControl(""),
      spirograph: new UntypedFormControl(""),
      heart_rate_device: new UntypedFormControl(""),
      ergometer: new UntypedFormControl(""),
      temperature: new UntypedFormControl(""),
      air_pressure: new UntypedFormControl(""),
      humidity: new UntypedFormControl(""),
      altitude: new UntypedFormControl(""),
      test_type: new UntypedFormControl(""),
      test_type_display: new UntypedFormControl(""),
      is_ppd: new UntypedFormControl(""),
      is_lactate: new UntypedFormControl(""),
    });

    this.isChecked =
      this.testForm.controls["auto_calc_body_com"]?.value == true;
    this.testForm.valueChanges.pipe(debounceTime(600)).subscribe((newVal) => {
      Object.keys(newVal).forEach((key: string): void => {
        this.testData[key] = newVal[key];
      });
    });
  }

  public setOriginalEquipmentList(equipmentList: any[]): void {
    this.originalDeviceList = equipmentList;
  }

  private initOptionAndValue(): Observable<any> {
    let apiCall;
    if (this.featureFlagService.getValue(FeatureFlagEnum.ENCRYPTION)) {
      apiCall = this.apiSecureService.secureGet<any>("sports/");
    } else {
      apiCall = this.apiService.get("sports/");
    }

    return forkJoin([
      this.apiService.get("tags/all"),
      apiCall,
      this.apiService.get("accounts/coaches/"),
      this.apiService.get("athletes/"),
      // TODO: move this
      this.showEquipment
        ? this.testEquipmentService.getTestEquipmentList()
        : of([]),
    ]).pipe(
      tap(
        ([
          resTags,
          resSports,
          resCoaches,
          resAthletes,
          equipmentList,
        ]: any): void => {
          this.setOriginalEquipmentList(equipmentList);
          this.testEquipmentTileList =
            this.testEquipmentService.generateEquipmentList(equipmentList);
          this.allTags = resTags?.results?.map((item: any) => {
            return this.mappingOptionValue(item, "name");
          });
          this.allSports = resSports?.results;

          if (this.data.isPPD) {
            this.allSports = resSports.results.filter(
              (sport: any) => sport.use_in_ppd,
            );
          }

          this.coaches = resCoaches?.results;
          this.filteredAthletes = resAthletes?.results;

          if (
            this.data.isVirtual ||
            ((this.data.isLactate || this.data.isPPD) && this.back)
          ) {
            const sport = this.allSports.find(
              (x: any) => x.id == this.testData.sport,
            );

            this.egbA = sport.ebg_factor_a;
            this.egbB = sport.ebg_factor_b;
            this.egbD = sport.ebg_factor_d;
            this.eqA = sport.o2_per_watt_eq_a;
            this.eqB = sport.o2_per_watt_eq_b;
            this.eqD = sport.o2_per_watt_eq_d;
            this.formConditions = this.fb.group({
              conditions: this.prepareEditConditions(
                this.testData.ebg_conditions,
              ),
            });

            this.testForm.markAllAsTouched();
            this.isChecked =
              this.testForm.controls["auto_calc_body_com"]?.value == true;
            this.testDataLoaded$.next(true);
          }
          this.initDataLoaded = true;
          this._setupAthleteFilter();
        },
      ),
    );
  }

  private _setupAthleteFilter(): void {
    this.athletes = this.filteredAthletes.slice();
    this._applyFilter();
  }

  private _applyFilter(): void {
    let search = this.searchAthleteCtrl.value;

    if (!search) {
      this.athletes = this.filteredAthletes.slice();
    } else {
      search = search.toLowerCase();

      this.athletes = this.filteredAthletes.filter((field: any) =>
        (field.first_name + " " + field.last_name)
          .toLowerCase()
          .includes(search),
      );
    }
  }

  public getTestsForAthlete(id: any): Observable<any[]> {
    this.apiService
      .get("files/select2/?ordering=-id&athlete_id=" + id)
      .subscribe((res: any): void => {
        this.filteredTests = res.map((item: any) => {
          return {
            value: item.id,
            name: item.file_name,
          };
        });
      });
    return of(this.filteredTests);
  }

  private mappingOptionValue(item: any, param: string) {
    return {
      value: item?.id,
      name: item[param],
    };
  }

  public prepareAutoCalcBodyCom() {
    if (this.isChecked) {
      return 1;
    } else return 0;
  }

  // Expert Settings overrides: only sent when the user actually fills them in,
  // otherwise the backend falls back to the sport defaults
  public static readonly EXPERT_OVERRIDE_FIELDS: string[] = [
    "au_override",
    "bu_override",
    "du_override",
    "o2_per_watt_eq_a",
    "o2_per_watt_eq_b",
    "o2_per_watt_eq_d",
  ];

  public clearExpertOverrides(): void {
    TestCreationComponent.EXPERT_OVERRIDE_FIELDS.forEach((field: string) => {
      this.testForm.controls[field]?.setValue("");
    });
  }

  public removeEmptyExpertOverrides(data: any): any {
    TestCreationComponent.EXPERT_OVERRIDE_FIELDS.forEach((field: string) => {
      const value = data[field];
      if (value === "" || value === null || value === undefined) {
        delete data[field];
      }
    });

    return data;
  }

  public prepareData() {
    let temp: string = this.formatDateDOB(
      this.testForm.get("test_date")?.value,
    );
    this.checkTestType();
    this.testForm
      .get("auto_calc_body_com")
      ?.setValue(this.prepareAutoCalcBodyCom());
    if (this.data.isPPD) {
      this.testForm
        .get("files")
        ?.setValue(
          this.testForm?.value?.files
            ? this.testForm?.value?.files.map((a: any) => a.value)
            : [],
        );
    }

    return this.removeEmptyExpertOverrides({
      ...this.testForm.value,
      equipment: JSON.stringify(this.payload.equipment),
      test_id: this.testId,
      created_by: this.auth.id,
      organization: this.auth.organization.id,
      tags: this.testForm?.value?.tags
        ? this.testForm?.value?.tags
            .filter((a: any) => a.value)
            .map((element: any) => element.value)
        : [],
      new_tags: this.newTagList || [],
      test_date: temp,
      excel_file: this.fileToUpload,
      ebg_conditions: this.prepareConditions(),
    });
  }

  public create(isViewAction: boolean, input: any): void {
    let apiCall;
    if (this.featureFlagService.getValue(FeatureFlagEnum.ENCRYPTION)) {
      apiCall = this.testService.createSecureTest(input);
    } else {
      apiCall = this.testService.createTest(input);
    }

    apiCall
      .pipe(
        catchError((error) => {
          this.snackBar.open(error, "OK", this.constant.TOAST_CONFIG.ERROR);
          throw error;
        }),
        finalize((): boolean => (this.isDataLoading = false)),
        map((res: any) =>
          res.response != null ? JSON.parse(res.response) : res,
        ),
        switchMap((obj: any) =>
          this.showCreationWarning(obj.warning_msg).pipe(map(() => obj)),
        ),
      )
      .subscribe((obj: any): void => {
        // TODO: Re-enable after testing: Additional requests for the equipment
        // if (additionalRequests.length > 0) {
        //   forkJoin(additionalRequests.map((request: () => Observable<any>) => request())).subscribe();
        // }
        if (isViewAction) {
          this.openOptimizationOptionsModal(true, obj.id);
        } else {
          if (this.data.isLactate) {
            this.router.navigateByUrl(
              "home/metabolic-profile/" + obj.id + "/shows?create=lactate",
            );
          }
          if (this.data.isPPD) {
            let createType: string | null =
              this.route.snapshot.queryParamMap.get("create");
            if (createType) {
              this.router.navigateByUrl(
                "home/ppd/" + obj.id + "/optimization?create=" + createType,
              );
            } else {
              this.router.navigateByUrl("home/ppd/" + obj.id + "/optimization");
            }
          }
          if (this.data.isManual || this.data.isVirtual) {
            this.openOptimizationOptionsModal(false, obj.id);
          }
        }
      });
  }

  /** Shows the API's optional warning; any way of closing the dialog continues the flow. */
  private showCreationWarning(
    message: string | null | undefined,
  ): Observable<unknown> {
    if (!message) return of(null);

    return this.dialog
      .open(ConfirmDialogComponent, {
        width: "400px",
        data: new ConfirmDialogModel(
          "Please note",
          message,
          "Continue",
          undefined,
          true,
        ),
      })
      .afterClosed();
  }

  public openOptimizationOptionsModal(
    isAcceptAndView: boolean,
    testId: number,
  ): void {
    const type = this.data.isVirtual
      ? AppConstants.TEST_TYPE_CHARS.virtual
      : AppConstants.TEST_TYPE_CHARS.manual;
    let modalUpdate: MatDialogRef<OptimizationOptionDialogComponent> =
      this.dialog.open(OptimizationOptionDialogComponent, {
        panelClass: "general-dialog",
        disableClose: true,
        data: {
          type,
        },
      });

    modalUpdate.afterClosed().subscribe((data: any): void => {
      console.log(data);
      if (!data || data.action !== "confirm") {
        return;
      }
      // TODO: try to combine these two functions
      if (type == AppConstants.TEST_TYPE_CHARS.manual) {
        console.log("manual");
        this.doManualTestOptimization(isAcceptAndView, data, testId);
      } else {
        this.doTestOptimization(isAcceptAndView, data, testId);
      }
    });
  }

  public doManualTestOptimization(
    isAcceptAndView: boolean,
    data: any,
    testId: number,
  ): void {
    let param_array: any[] = [];
    let params: string = "";

    if (data && data.action == "confirm") {
      if (data.productId) {
        param_array.push("product=" + data.productId);
      }
      if (data.planId) {
        param_array.push("plan=" + data.planId);
      }
      params = param_array.join("&");
    }
    console.log(params);
    const api = `tests/${testId}/accept/${params ? "?" + params : ""}`;
    this.apiService
      .put(api, {})
      .pipe(
        catchError((error) => {
          // Our API service seems to sanitize these errors so error is just a string message
          // See: api.service.ts::handleError
          const paramErrors = error.split(" ").slice(1); // Ignore the initial 'Error: ' prefix
          if (error === "Error: TEST_ALREADY_ACTIVE") {
            this.router.navigateByUrl("home/metabolic-profile/" + testId + "/");
          }
          if (paramErrors.length > 0 && paramErrors.indexOf("500") > -1) {
            // const paramErrors = error.error.detail.split('_');
            let testName = "";
            if (paramErrors[1] == "newtype") testName = "PPD test";
            else if (paramErrors[1] == AppConstants.TEST_TYPES.LACTATE)
              testName = "Lactate test";
            else if (paramErrors[1] == AppConstants.TEST_TYPES.VIRTUAL)
              testName = "Virtual test";
            else if (paramErrors[1] == AppConstants.TEST_TYPES.CRITICAL_POWER)
              testName = "critical power test";
            else if (paramErrors[1] == AppConstants.TEST_TYPES.MANUAL)
              testName = "Manual test";
            this.popupPurchase(testName, error);
          } else {
            this.snackBar.open(error, "OK", this.constant.TOAST_CONFIG.ERROR);
          }
          throw error;
        }),
      )
      .subscribe((): void => {
        this.snackBar.open(
          "Item updated successfully.",
          "OK",
          this.constant.TOAST_CONFIG.SUCCESS,
        );
        this.handleRedirect(testId, isAcceptAndView);
      });
  }

  doTestOptimization(isAcceptView: boolean, data: any, testId: number) {
    let param_array = [];
    let params = "";

    if (data && data.action == "confirm") {
      if (data.productId) {
        param_array.push("product=" + data.productId);
      }
      if (data.planId) {
        param_array.push("plan=" + data.planId);
      }
      params = param_array.join("&");
    }

    const api: string = `tests/${testId}/accept_multiple/${params ? "?" + params : ""}`;
    this.apiService.get(api, false).subscribe(
      (res: any): void => {
        if (!res?.data?.is_multiple) {
          this.doAcceptAndViewOPtimization(true, data, testId);
        }
      },
      (error): void => {
        if (error === "Error: Test already active") {
          this.router.navigateByUrl("/home/apc/setcard/" + testId);
        }
      },
    );
  }

  public doAcceptAndViewOPtimization(
    isAcceptView: boolean,
    data: any,
    testId: number,
  ): void {
    let param_array: any[] = [];
    let params: string = "";

    if (data && data.action == "confirm") {
      if (data.productId) {
        param_array.push("product=" + data.productId);
      }
      if (data.planId) {
        param_array.push("plan=" + data.planId);
      }
      params = param_array.join("&");
    }
    const api: string = `tests/${testId}/accept/${params ? "?" + params : ""}`;
    this.apiService
      .put(api, {})
      .pipe(
        catchError((error) => {
          const paramErrors = error.split(" ").slice(1); // Ignore the initial 'Error: ' prefix
          if (error === "Error: TEST_ALREADY_ACTIVE") {
            this.router.navigateByUrl("home/metabolic-profile/" + testId + "/");
          }
          if (paramErrors.length > 0 && paramErrors.indexOf("500") > -1) {
            let testName: string = "";
            if (paramErrors[1] == "newtype") testName = "PPD test";
            else if (paramErrors[1] == AppConstants.TEST_TYPES.LACTATE)
              testName = "Lactate test";
            else if (paramErrors[1] == AppConstants.TEST_TYPES.VIRTUAL)
              testName = "Virtual test";
            else if (paramErrors[1] == AppConstants.TEST_TYPES.MANUAL)
              testName = "Manual test";
            else if (paramErrors[1] == AppConstants.TEST_TYPES.CRITICAL_POWER)
              testName = "critical power test";
            this.popupPurchase(testName, error);
          } else {
            this.snackBar.open(error, "OK", this.constant.TOAST_CONFIG.ERROR);
          }
          throw error;
        }),
      )
      .subscribe((): void => {
        this.snackBar.open(
          "Item updated successfully.",
          "OK",
          this.constant.TOAST_CONFIG.SUCCESS,
        );
        this.handleRedirect(testId, isAcceptView);
      });
  }

  private popupPurchase(testName: string, error: string): void {
    const paramErrors: string[] = error.split(" ");
    const message = this.testService.excessTestConfirmationText(
      testName,
      error,
    );
    const dialogData: ConfirmDialogModel = new ConfirmDialogModel(
      "Confirmation",
      message,
    );
    const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(
      ConfirmDialogComponent,
      {
        width: "400px",
        data: dialogData,
      },
    );

    dialogRef.afterClosed().subscribe((result: any): void => {
      if (result) {
        const params1: string =
          "?plan=" +
          paramErrors[3] +
          "&type=" +
          paramErrors[2] +
          "&new_id=" +
          paramErrors[4];
        const apiUrl: string = String.Format(
          this.constant.API.INVOICES.PURCHASE_EXCEED,
          params1,
        );
        this.apiService.post(apiUrl, {}).subscribe(
          (): void => {
            this.handleRedirect(this.testId, true);
          },
          (errorPurchase): void => {
            this.snackBar.open(
              errorPurchase,
              "OK",
              this.constant.TOAST_CONFIG.ERROR,
            );
          },
        );
      }
    });
  }

  public handleRedirect(testId: number, isAcceptView: boolean): void {
    if (!isAcceptView) {
      const createType: string | null =
        this.route.snapshot.queryParamMap.get("create");
      if (createType) {
        this.router.navigate(["home/manage/users"]);
        setTimeout(() => {
          this.router.navigate(["home/manage/tests"]);
        });
      }
    } else {
      this.router.navigateByUrl("home/apc/setcard/" + testId);
    }
  }

  public update(): void {
    // These fields are calculated by the backend - no need to send from here
    const {
      vo2tot_coefficients,
      simulation_results,
      sse_report,
      plot_data,
      test_raw_data_json,
      ...testData
    } = this.testData;

    if (this.data.isPPD) {
      testData.files = testData.files.map((file: any) => file.value);
    }

    this.removeEmptyExpertOverrides(testData);

    this.testService
      .updateTest(this.testId, testData)
      .pipe(
        catchError((error) => {
          this.snackBar.open(error, "OK", this.constant.TOAST_CONFIG.ERROR);
          throw error;
        }),
        finalize((): boolean => (this.isDataLoading = false)),
      )
      .subscribe((res: any): void => {
        const obj = JSON.parse(res.response);
        if (this.data.isLactate) {
          this.router.navigateByUrl(
            "home/metabolic-profile/" + obj.id + "/shows?create=lactate",
          );
        }
        if (this.data.isPPD) {
          this.router.navigateByUrl(
            "home/ppd/" + obj.id + "/optimization?create=newtype",
          );
        }
      });
  }

  public save(isViewAction: boolean): void {
    if (this.testForm.invalid) {
      this.testForm.markAllAsTouched();
      this.snackBar.open(
        "Please check the fields and try again.",
        "OK",
        this.constant.TOAST_CONFIG.ERROR,
      );
      return;
    }
    if (this.data.isLactate && !this.fileToUpload) {
      this.snackBar.open(
        "Lactate Test file is required.",
        "OK",
        this.constant.TOAST_CONFIG.ERROR,
      );

      return;
    }

    if (!this.featureFlagService.getValue(FeatureFlagEnum.PPD_V3)) {
      if (this.data.isPPD && !this.testForm.get("files")?.value.length) {
        this.snackBar.open(
          "Fit file is required.",
          "OK",
          this.constant.TOAST_CONFIG.ERROR,
        );

        return;
      }
    }

    if (this.currentAthlete) {
      const athleteWeight: number = this.currentAthlete.default_weight;
      const athleteHeight: number = this.currentAthlete.default_height;
      const currentWeight: number = this.testForm.get("mass")?.value;
      const currentHeight: number = this.testForm.get("height")?.value;
      const isWeightChanged: boolean = athleteWeight !== currentWeight;
      const isHeightChanged: boolean = athleteHeight !== currentHeight;

      if (isWeightChanged || isHeightChanged) {
        let modalUpdate: MatDialogRef<DefaultSettingDialogComponent> =
          this.dialog.open(DefaultSettingDialogComponent, {
            width: "520px",
            disableClose: true,
            data: {
              isWeightChanged,
              isHeightChanged,
            },
          });

        modalUpdate
          .afterClosed()
          .pipe(
            switchMap((response: any) => {
              const athleteData: Record<string, number> = {};
              if (response.weight) {
                athleteData["default_weight"] = currentWeight;
              }

              if (response.height) {
                athleteData["default_height"] = currentHeight;
              }

              if (response.weight || response.height) {
                return this.athletesService.partialUpdate(
                  this.currentAthlete.id,
                  athleteData,
                );
              } else {
                return of("");
              }
            }),
          )
          .subscribe({
            next: (): void => {
              this.updateOrCreateTest(isViewAction);
            },
            error: (err: any) => {
              this.snackBar.open(err, "OK", AppConstants.TOAST_CONFIG.ERROR);
            },
          });
      } else {
        this.updateOrCreateTest(isViewAction);
      }
    } else {
      this.updateOrCreateTest(isViewAction);
    }
  }

  public updateOrCreateTest(isViewAction: boolean): void {
    const input = this.prepareData();
    if ((this.data.isPPD || this.data.isLactate) && this.back) {
      this.update();
    } else {
      this.create(isViewAction, input);
    }
  }

  get conditions(): UntypedFormArray {
    return this.formConditions.get("conditions") as UntypedFormArray;
  }

  private isGlobalCondition(condition: Record<string, any>): boolean {
    const fields = [
      'ebg_factor_a',
      'ebg_factor_b',
      'ebg_factor_d',
      'o2_per_watt_eq_a',
      'o2_per_watt_eq_b',
      'o2_per_watt_eq_d',
    ];
    return !(fields.some((field) => condition[field] != null));
  }

  public newConditions(data?: any): UntypedFormGroup {
    return this.fb.group({
      name: new UntypedFormControl(data?.name, [
        Validators.required,
        Validators.pattern(this.constant.REGEX.NUMBER_LETTER_DASH_UNDERSCORE),
      ]),
      ebg_factor_a: new UntypedFormControl(data?.ebg_factor_a, [
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]),
      ebg_factor_b: new UntypedFormControl(data?.ebg_factor_b, [
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]),
      ebg_factor_d: new UntypedFormControl(data?.ebg_factor_d, [
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]),
      o2_per_watt_eq_a: new UntypedFormControl(data?.o2_per_watt_eq_a, [
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]),
      o2_per_watt_eq_b: new UntypedFormControl(data?.o2_per_watt_eq_b, [
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]),
      o2_per_watt_eq_d: new UntypedFormControl(data?.o2_per_watt_eq_d, [
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]),
      created_by: new UntypedFormControl(data?.created_by ?? ""),
      is_global: new UntypedFormControl(this.isGlobalCondition(data)),
    });
  }

  public addCondition(): void {
    const sport = this.allSports.find(
      (x: any): boolean => x.id == this.testForm.get("sport")?.value,
    );

    if (sport == null) {
      this.snackBar.open(
        "Please select Sport",
        "OK",
        AppConstants.TOAST_CONFIG.ERROR,
      );
      return;
    }

    this.conditions.push(
      this.newConditions({
        ebg_factor_a: sport.ebg_factor_a,
        ebg_factor_b: sport.ebg_factor_b,
        ebg_factor_d: sport.ebg_factor_d,
        o2_per_watt_eq_a: sport.o2_per_watt_eq_a,
        o2_per_watt_eq_b: sport.o2_per_watt_eq_b,
        o2_per_watt_eq_d: sport.o2_per_watt_eq_d,
        created_by: this.auth.id,
      }),
    );
  }

  public removeCondition(i: number): void {
    this.conditions.removeAt(i);
  }

  public prepareConditions(): string {
    const conditions = this.formConditions.getRawValue().conditions.map((condition: Record<string, any>) =>
      condition.is_global ? {name: condition.name} : condition
    );

    return JSON.stringify(conditions);
  }

  public onChangeSport(newValue: any): void {
    const sport = this.allSports.find((x: any): boolean => x.id == newValue);
    if (sport == null) {
      return;
    }

    this.formConditions = this.fb.group({ conditions: this.fb.array([]) });
    this.selectedSportSlug = sport != null ? sport.feature_slug : undefined;
    this.loadEquipment();

    // Overrides stay empty so the sport defaults below are used unless the
    // user explicitly enters a value
    this.clearExpertOverrides();

    this.egbA = sport.ebg_factor_a;
    this.egbB = sport.ebg_factor_b;
    this.egbD = sport.ebg_factor_d;
    this.eqA = sport.o2_per_watt_eq_a;
    this.eqB = sport.o2_per_watt_eq_b;
    this.eqD = sport.o2_per_watt_eq_d;

    const dataCondition = JSON.parse(sport.ebg_conditions);
    if (dataCondition && dataCondition.length) {
      dataCondition.forEach((item: any) => {
        this.conditions.push(this.newConditions(item));
      });
    }

    this.currentSport = sport;
  }

  public setDefaultAthleteCharacteristics(
    athleteResponse: any,
    formControlName: string,
    property: string,
  ): void {
    if (athleteResponse[property]) {
      this.testForm.get(formControlName)?.setValue(athleteResponse[property]);
    }
  }

  public resetFormControlByName(name: string): void {
    this.testForm.get(name)?.reset();
  }

  public onChangeAthlete(newValue: any): void {
    if (newValue) {
      const athlete = this.filteredAthletes.find(
        (x: any): boolean => x.id == newValue,
      );
      if (athlete != null) {
        this.selectedAthlete = athlete.id;
        this.loadEquipment();
      }
      this.currentAthlete = athlete;
      this.currentAthlete["coaches"] = this.auth.id;
      if (
        athlete &&
        athlete.hasOwnProperty("latest_sport_id") &&
        athlete.latest_sport_id
      ) {
        this.onChangeSport(athlete.latest_sport_id);
      }
      this.resetFormControlByName("sport");
      this.resetFormControlByName("proz_body_fat_100");
      this.resetFormControlByName("mass");
      this.resetFormControlByName("height");

      this.setDefaultAthleteCharacteristics(
        athlete,
        "sport",
        "latest_sport_id",
      );
      this.setDefaultAthleteCharacteristics(
        athlete,
        "proz_body_fat_100",
        "latest_body_fat",
      );
      this.setDefaultAthleteCharacteristics(athlete, "mass", "default_weight");
      this.setDefaultAthleteCharacteristics(
        athlete,
        "height",
        "default_height",
      );

      if (this.data.isPPD) {
        this.getTestsForAthlete(athlete.id);
        this.testForm.get("files")?.setValue([]);
      }

    }
  }

  public checkTestType(): void {
    if (this.data.isManual == true) {
      this.testForm.get("is_ppd")?.setValue(false);
      this.testForm.get("is_lactate")?.setValue(false);
      this.testForm.get("test_type")?.setValue("manual");
      this.testForm.get("test_type_display")?.setValue("Manual");
    } else if (this.data.isPPD == true) {
      this.testForm.get("is_ppd")?.setValue(true);
      this.testForm.get("is_lactate")?.setValue(false);
      this.testForm.get("test_type")?.setValue("newtype");
      this.testForm.get("test_type_display")?.setValue("PPD");
    } else if (this.data.isVirtual == true) {
      this.testForm.get("is_ppd")?.setValue(false);
      this.testForm.get("is_lactate")?.setValue(false);
      this.testForm.get("test_type")?.setValue("virtual");
      this.testForm.get("test_type_display")?.setValue("Virtual");
    } else if (this.data.isLactate == true) {
      this.testForm.get("is_ppd")?.setValue(false);
      this.testForm.get("is_lactate")?.setValue(true);
      this.testForm.get("test_type")?.setValue("lactate");
      this.testForm.get("test_type_display")?.setValue("Lactate");
    }
  }

  public onSelectTag(value: any): void {
    this.testForm.get("tags")?.setValue(value);
  }

  public onSelectNewTag(value: any): void {
    this.newTagList.push(value);
  }

  public onRemoveNewTag(tag: any): void {
    const indexForRemove: number = this.newTagList.indexOf(tag.option.name);
    this.testForm.get("tags")?.setValue(tag.inputValue);
    if (indexForRemove > -1) {
      this.newTagList.splice(indexForRemove, 1);
    }
  }

  public onSelectFile(value: any): void {
    this.testForm.get("files")?.setValue(value);
  }

  public saveCopyBodyComposition(): void {
    this.selectedValueCopy = {
      proz_body_water_100_override: this.testForm.get(
        "proz_body_water_100_override",
      )?.value,
      proz_body_muscle_100_override: this.testForm.get(
        "proz_body_muscle_100_override",
      )?.value,
      proz_muscle_mass_used_100_override: this.testForm.get(
        "proz_muscle_mass_used_100_override",
      )?.value,
    };
  }

  public restoreBodyComposition(): void {
    this.testForm
      .get("proz_body_water_100_override")
      ?.setValue(this.selectedValueCopy.proz_body_water_100_override);
    this.testForm
      .get("proz_body_muscle_100_override")
      ?.setValue(this.selectedValueCopy.proz_body_muscle_100_override);
    this.testForm
      .get("proz_muscle_mass_used_100_override")
      ?.setValue(this.selectedValueCopy.proz_muscle_mass_used_100_override);
  }

  public showOptions(event: MatCheckboxChange): void {
    this.isChecked = event.checked;
    if (!event.checked) {
      this.saveCopyBodyComposition();
    } else if (event.checked) {
      this.restoreBodyComposition();
    }
  }

  public formatDateDOB(date: any): string {
    let d: Date = new Date(date),
      month: string = "" + (d.getMonth() + 1),
      day: string = "" + d.getDate(),
      year: number = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  public handleFileInput(data: any): void {
    this.fileToUpload = data.target.files[0];
    if ("excel_file" in this.testData) {
      this.testData.excel_file = this.fileToUpload;
    }
  }

  public handleDragFileInput(data: any): void {
    this.fileToUpload = data[0].file;
    if ("excel_file" in this.testData) {
      this.testData.excel_file = this.fileToUpload;
    }
  }

  public closeDialog(): void {
    this.router.navigate(["home/dashboard"]);
  }

  public validateTags(control: AbstractControl): any {
    if (control.value) {
      const isString: boolean =
        Object.prototype.toString.call(control.value) === "[object String]";

      return !isString
        ? null
        : { errorTags: "Press enter button to complete adding tag" };
    }

    return null;
  }

  public changeFile(event: any): void {
    if (this.validateBeforeUploadFile() && event?.target?.files) {
      this.listFileUpload = event?.target?.files;
      this.uploadFile();
    } else {
      this.fileUpload.nativeElement.value = "";
    }
  }

  private validateBeforeUploadFile(): boolean {
    if (!this.testForm.get("athlete")?.value) {
      this.snackBar.open(
        "Have to select an athlete before upload files.",
        "OK",
        this.constant.TOAST_CONFIG.ERROR,
      );
      return false;
    } else if (!this.testForm.get("mass")?.value) {
      this.snackBar.open(
        "Have to input weight before upload files.",
        "OK",
        this.constant.TOAST_CONFIG.ERROR,
      );
      return false;
    }
    return true;
  }

  public filesDropped(event: FileHandle[]): void {
    if (this.validateBeforeUploadFile()) {
      this.listFileUpload = event.map((item: FileHandle) => item.file);
      this.uploadFile();
    }
  }

  public uploadFile(): void {
    // preventing upload if more than 5 files
    if (!this.listFileUpload || this.listFileUpload.length === 0) {
      this.snackBar.open(
        "Select a file to upload.",
        "OK",
        this.constant.TOAST_CONFIG.ERROR,
      );
      return;
    }
    if (this.listFileUpload && this.listFileUpload.length > 20) {
      this.snackBar.open(
        "At the moment the service is not able to process more than 20 files",
        "OK",
        this.constant.TOAST_CONFIG.ERROR,
      );
      return;
    }

    // Check file extension
    const extensionRegex: RegExp = /(?:\.([^.]+))?$/;
    let isValidExtension: boolean = true;
    let invalidFileName: string = "";
    for (let file of this.listFileUpload) {
      const ext: string | undefined = extensionRegex.exec(file.name)?.[1];

      if (ext && !["FIT", "TXT"].includes(ext.toUpperCase())) {
        invalidFileName = file.name;
        isValidExtension = false;
        break;
      }
    }
    if (!isValidExtension) {
      this.snackBar.open(
        invalidFileName + " is not a valid .fit or .txt file.",
        "OK",
        this.constant.TOAST_CONFIG.ERROR,
      );
      return;
    }
    for (let file of this.listFileUpload) {
      const reader: FileReader = new FileReader();
      const file_name = file.name;
      const athlete_id = this.testForm.get("athlete")?.value;
      const weight = this.testForm.get("mass")?.value || 0;
      reader.onload = (e: any): void => {
        const ppdv3Flag = this.featureFlagService.getValue(
          FeatureFlagEnum.PPD_V3,
        );
        if (!ppdv3Flag) {
          this.fitFileParserService.parseFileBuffer(
            e.target.result,
            file_name,
            athlete_id,
            weight,
            this.callBE.bind(this),
          );
        } else {
          const ext: string | undefined = extensionRegex.exec(file.name)?.[1];
          if (ext?.toUpperCase() == "TXT") {
            this.txtFileParserService.parseTxtFile(
              e.target.result,
              file_name,
              athlete_id,
              weight,
              this.callBE.bind(this),
              this.currentSport,
            );
          } else if (ext?.toUpperCase() == "FIT") {
            this.fitFileParserService.parseFileBuffer(
              e.target.result,
              file_name,
              athlete_id,
              weight,
              this.callBE.bind(this),
              this.currentSport,
            );
          }
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }

  private callBE(model: any): void {
    this.filesService.create(model).subscribe(
      (res: any): void => {
        this.getTestsForAthlete(res.athlete).subscribe((): void => {
          const obj = {
            name: res.file_name,
            value: res.id,
          };
          const array = this.testForm.get("files")?.value;
          array.push(obj);
          this.testForm.get("files")?.setValue(array);
        });
        this.snackBar.open(
          "Item created successfully",
          "OK",
          this.constant.TOAST_CONFIG.SUCCESS,
        );
        this.fileUpload.nativeElement.value = "";
        this.listFileUpload = [];
      },
      (error): void => {
        this.fileUpload.nativeElement.value = "";
        this.listFileUpload = [];
        this.snackBar.open(error, "OK", this.constant.TOAST_CONFIG.ERROR);
      },
    );
  }

  private loadEquipment(): void {
    if (!this.showEquipment) {
      return;
    }

    if (!this.selectedAthlete) {
      return;
    }

    if (!this.selectedSportSlug) {
      return;
    }

    if (
      this.equipmentLoadedParams.athlete === this.selectedAthlete &&
      this.equipmentLoadedParams.sportSlug === this.selectedSportSlug
    ) {
      // Equipment already loaded for this athlete/sport combination
      return;
    }

    let source: TestEqupimentListSource;
    if (this.data.isPPD) {
      source = "ppd";
    } else if (this.data.isLactate) {
      source = "lactate";
    } else {
      return;
    }

    console.info(
      `Loading equipment for source ${source} and athlete ${this.selectedAthlete} and sport ${this.selectedSportSlug}`,
    );
    const athlete = this.filteredAthletes.find(
      (x: any): boolean => x.id == this.selectedAthlete,
    );
    const athleteEquipment = athlete ? (athlete.equipment ?? []) : [];
    this.equipmentLoadedParams.athlete = this.selectedAthlete;
    this.equipmentLoadedParams.sportSlug = this.selectedSportSlug;
    this.testEquipmentService
      .getTestEquipmentList({
        source: source,
        sport_slug: this.selectedSportSlug!,
        athlete: this.selectedAthlete!,
      })
      .subscribe({
        next: (equipmentList: any) => {
          this.setOriginalEquipmentList(equipmentList);
          this.testEquipmentTileList =
            this.testEquipmentService.generateEquipmentListWithDefaults(
              equipmentList,
              athleteEquipment,
              this.auth.organization.id,
            );
        },
      });
  }
}
